import React from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@dwelvio/auth-react';
import { Box, Stack, Typography } from '@mui/material';
import { TopBar } from './top-bar';
import { IUserData } from '../types';
import { appPaths } from '../app-paths';

interface BasePageProps {
  title?: string;
  children: React.ReactNode;
  loading?: boolean;
  errorMsg?: string;
  requireAuth?: boolean; // if true, will redirect to signin page if not authenticated
}

export function BasePage(props: BasePageProps) {
  const { title, children, loading, errorMsg, requireAuth } = props;

  const navigate = useNavigate();
  const { logout, isLoading: authLoading, isAuthenticated } = useAuth<IUserData>();

  const handleLogout = () => {
    logout();
    navigate(appPaths.signin.navigate());
  };

  if (requireAuth && !isAuthenticated && !authLoading) {
    navigate(appPaths.signin.navigate({ refUrl: window.location.pathname }));
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        maxWidth: '1024px',
        margin: '0 auto',
        minWidth: '350px',
      }}
    >
      <Stack direction="column" sx={{ height: '100%' }}>
        <TopBar onLogout={handleLogout} maxWidth="1024px" />

        <Box id="page-content" sx={{ py: 2, px: 3, width: '100%' }}>
          <Stack direction="row" spacing={0} sx={{ alignItems: 'center', mb: 2 }}>
            {title && (
              <Typography variant="h1" sx={{ margin: 0, marginLeft: '0.5rem' }}>
                {title}
              </Typography>
            )}
          </Stack>
          {(loading || (requireAuth && authLoading)) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="h2">Loading...</Typography>
            </Box>
          )}
          {errorMsg && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="h2" color="error">
                {errorMsg}
              </Typography>
            </Box>
          )}
          {!loading && !errorMsg && children}
        </Box>
      </Stack>
    </Box>
  );
}
